import { ComponentProps } from "react";
import UpdatedTime from "@atoms/Time/UpdatedTime";
import SubscriberFlag from "@components/ArticleMeta/SubscriberFlag";
import IconFactory from "@components/Icon/IconFactory";
import { IconTypeEnum } from "@components/Icon/utils/types";
import { useUserInfo } from "@features/mySph/hooks/useUserInfo";
import CardPartnership from "@particles/card/components/CardPartnership";
import CardTitle from "@particles/card/components/CardTitle";
import { cn } from "@util/helpers";

type PodcastsSectionStoriesCardProps = {
  /** The props to display the title */
  titleProps: ComponentProps<typeof CardTitle>;
  /** The props to display the partnership info */
  partenrshipProps?: ComponentProps<typeof CardPartnership>;
  /** The props to display the updated time */
  timeProps?: ComponentProps<typeof UpdatedTime>;
  /** Additional class name for the container */
  className?: string;
  /** Whether to display the paywall indicator */
  displayPaywall?: boolean;
};

/**
 * Podcast Section Stories Card Component
 * Figma: https://www.figma.com/design/xsbEGttNPBTNUi388qtSJD/PODCASTS--WEB-?node-id=3397-329006&t=Rc3p8bM01UozhUND-4
 */
const PodcastsSectionStoriesCard = ({
  titleProps,
  partenrshipProps,
  timeProps,
  className,
  displayPaywall,
}: PodcastsSectionStoriesCardProps): React.ReactElement => {
  const { isSubscriber } = useUserInfo();

  return (
    <div
      data-testid="podcasts-section-stories-card-component"
      className={cn("flex flex-wrap", className)}
    >
      <IconFactory type={IconTypeEnum.headphone} x={9} y={9} size={20} />

      <CardTitle
        {...titleProps}
        className="order-1 ml-1 flex basis-[calc(100%-20px-0.25rem)] gap-1 font-lct text-base font-bold -tracking-2% text-gray-850 lg:max-w-[460px]"
        spanClassName="lg:line-clamp-1"
      />

      {isSubscriber && displayPaywall ? (
        <SubscriberFlag
          rootClassName={cn(
            "mt-2 mb-0 lg:ml-0 ml-auto order-3 basis-[calc(100%-20px-0.25rem)] lg:basis-full"
          )}
        />
      ) : null}

      {timeProps ? (
        <UpdatedTime
          {...timeProps}
          className="order-4 ml-auto mt-3 basis-[calc(100%-20px-0.25rem)] font-poppins text-7xs text-gray-575 lg:order-2 lg:mt-0 lg:basis-auto"
          dateFormat="ddd, MMM D, YYYY"
        />
      ) : null}

      <div className="order-3 ml-auto basis-[calc(100%-20px-0.25rem)] lg:order-4 lg:basis-full">
        <CardPartnership
          {...partenrshipProps}
          className={cn(
            "mb-1 mt-1 w-fit border border-gray-175 px-2 py-1 font-poppins text-4xs font-light text-gray-175 lg:mt-2"
          )}
          messageClassName="text-gray-575"
          nameClassName="text-gray-650"
        />
      </div>
    </div>
  );
};

export default PodcastsSectionStoriesCard;
